/**
 * demoMode.ts (shared) — unauthenticated, unguessable DEMO route for the React
 * apps. Canonical home (used by useClientData + the Control Center App; the Admin
 * app has a local copy with the SAME key — TODO unify onto this module).
 *
 * Purpose (John, 2026-06-13): a no-login showcase to screen-share with partners /
 * franchise prospects on Zoom. Open `/<app>/?demo=<DEMO_ACCESS_KEY>` once; the key
 * is long + unguessable; the bare route shows the normal login wall. On match we
 * persist a session flag so SPA navigation keeps demo mode. Demo mode bypasses the
 * auth guard and renders representative data — it NEVER authenticates, so Firestore
 * reads stay denied and no real client data is exposed (the Control Center serves a
 * bundled fixture; see demoClientData.ts). Page is marked noindex.
 *
 * Rotate the key (revoke all demo links): change DEMO_ACCESS_KEY + redeploy.
 */

/**
 * Long, unguessable demo key — THE only copy (unified 2026-07-20).
 *
 * Previously this said "MUST match app/admin/src/demoMode.ts until unified".
 * That file does not exist; the real second copy was a private DEMO_KEY in
 * app/admin/src/pages/Dashboard.tsx, which builds the Command Center demo
 * links. So the note pointed at the wrong file AND the rotation instruction
 * above would have missed the one place John actually clicks from. Both now
 * import from here.
 */
export const DEMO_ACCESS_KEY = 'bas-demo-7f3k9x2p5q8w1m4z6v0n8j';

const SESSION_FLAG = 'bas_demo_mode';

/** Check URL for ?demo=<key>; persist for the SPA session; return active state. */
export function detectAndPersistDemoMode(): boolean {
  try {
    const param = new URLSearchParams(window.location.search).get('demo');
    if (param && param === DEMO_ACCESS_KEY) {
      sessionStorage.setItem(SESSION_FLAG, '1');
    }
    return sessionStorage.getItem(SESSION_FLAG) === '1';
  } catch {
    return false;
  }
}

/** True when the current SPA session is in demo mode. */
export function isDemoMode(): boolean {
  try {
    return sessionStorage.getItem(SESSION_FLAG) === '1';
  } catch {
    return false;
  }
}

/** Inject a noindex robots meta so the demo route is never crawled/indexed. */
export function applyDemoNoIndex(): void {
  try {
    if (typeof document === 'undefined') return;
    if (document.querySelector('meta[name="robots"][data-demo]')) return;
    const m = document.createElement('meta');
    m.name = 'robots';
    m.content = 'noindex, nofollow';
    m.setAttribute('data-demo', '1');
    document.head.appendChild(m);
  } catch {
    /* no-op */
  }
}
